<template>
  <session-content-container :title="title">
    <template #right>
      <v-btn
        color="accent"
        small
        dark
        @click="drawListOpened = true"
      >
        <span v-text="$t('animation.multiproject.general.draw')" />
        <v-icon right>
          mdi-dice-multiple
        </v-icon>
      </v-btn>
    </template>

    <template v-if="game">
      <v-row no-gutters class="pb-3">
        <v-col
          v-for="(resource, index) in game.resourceList"
          :key="`resource-${index}`"
          class="px-1 pb-1"
          cols="12"
          sm="6"
          md="3"
        >
          <kpi-card :value="resource" />
        </v-col>
      </v-row>

      <player-card
        v-for="(player, playerIndex) in sortedPlayerList"
        :key="`player-${playerIndex}`"
        :value="player"
      />
    </template>

    <draw-container
      v-if="game"
      :visible.sync="drawListOpened"
      :game-id="gameId"
      :current-round="game.currentRound"
    />
  </session-content-container>
</template>

<script>
import _throttle from 'lodash/throttle'
import { mapActions } from 'vuex'
import Repositories from 'src/repositories'
import SessionContentContainer from '../SessionContentContainer'
import PlayerCard from '../../components/multi-project/PlayerCard'
import DrawContainer from './DrawContainer'
import KpiCard from '../../components/multi-project/KpiCard'

export default {
  name: 'MultiProjectStandardContainer',
  components: {
    SessionContentContainer,
    PlayerCard,
    DrawContainer,
    KpiCard,
  },
  props: {
    sessionId: {
      type: String,
      required: true,
    },
    gameId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    game: null,
    loading: true,
    drawListOpened: false,
  }),
  computed: {
    title () {
      if (this.loading) {
        return this.$t('general.loading')
      }
      const title = this.$t('animation.menu.games.multiProjectStandard')
      const round = this.$tc('animation.multiproject.general.round')

      return `${title} - ${round} ${this.game.currentRound}`
    },
    sortedPlayerList () {
      return this.game.playerList
        .slice()
        .sort((a, b) => a.projectOrder - b.projectOrder)
    },
  },
  sockets: {
    trainingSessionPlayerLoggedIn ({ idSession }) {
      if (this.sessionId === idSession) {
        this.fetchData()
      }
    },
    async multiProjectSessionBulkChanges ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerWaitForStart ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCantStartProject ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerMustStartProject ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCanStartProjectBlueReady ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerResourcesSelection ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerWaitForResourcesCapacity ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCanGetCapacity ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCanStartProjectBlueOverCapacity ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerCapacityReceived ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerMustChangeEndDate ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerWaitToStartNewWeek ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerReadyToStartNewWeek ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerReadyToStartNewWeekProjectNotStarted ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
    multiProjectSessionPlayerProjectFinished ({ multiProjectSessionId }) {
      if (multiProjectSessionId === this.gameId) {
        this.fetchData()
      }
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler () {
        this.fetchData()
      },
    },
  },
  methods: {
    ...mapActions('ui', {
      showSuccessNotification: 'showSuccessNotification',
      showErrorNotification: 'showErrorNotification',
    }),
    fetchData: _throttle(async function fetchData () {
      this.game = await Repositories.animation.multiproject.get(this.gameId)
      // Show message only during the first load (not on data refresh)
      this.loading = false
    }, 200, { leading: true, trailing: true }),
    async sendPlayerAction (playerId, actionName) {
      try {
        await Repositories.animation.multiproject.sendAction({
          gameId: this.gameId,
          playerId,
          actionName,
        })
        this.fetchData()
      }
      catch (error) {
        this.showErrorNotification(Repositories.parseErrorMessage(error))
        console.error(error)
      }
    },
  },
  provide () {
    return {
      sendPlayerAction: this.sendPlayerAction,
    }
  },
}
</script>
